import {
  getLeoUuid,
  LeoUUID,
  getInt32,
  getUrl,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
/* eslint-disable import/export */
export abstract class GetPresignedDownloadURLForIndividualSubmittedResponsesRPC implements
  LeoRPC<GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.Request, GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.Response, GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.Errors.Errors>
{
  abstract execute(
    request: GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.Request
  ): Promise<LeoRPCResult<GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.Response, GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.Errors.Errors>>;
}

export namespace GetPresignedDownloadURLForIndividualSubmittedResponsesRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    projectId: string;
    respondentId: number;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isGetPresignedDownloadURLForIndividualSubmittedResponsesRPCRequest: boolean = true;
  
    readonly projectId: LeoUUID;
    readonly respondentId: number;
  
  
    constructor(
    projectId: LeoUUID, 
    respondentId: number
    ) {
      this.projectId = projectId;
      this.respondentId = respondentId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      const respondentId = getInt32(dto, "respondentId", InvalidRequestRespondentIdError);
      return new Request(
        projectId,
        respondentId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          projectId: this.projectId.toDTO(),
          respondentId: this.respondentId
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `GetPresignedDownloadURLForIndividualSubmittedResponsesRPC(projectId='${this.projectId}',respondentId='${this.respondentId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {
    documentURL: string;
  }
  
  export class Response implements LeoRPCResponse {
    readonly isGetPresignedDownloadURLForIndividualSubmittedResponsesResponse: boolean = true;
  
    readonly documentURL: URL;
  
  
    constructor(
    documentURL: URL
    ) {
      this.documentURL = documentURL;
    }
  
    static fromDTO(dto: ResponseDTO): Response {
      const documentURL = getUrl(dto, "documentURL", InvalidResponseDocumentUrlError);
      return new Response(
        documentURL
      );
    }
  
    toDTO(): ResponseDTO {
      const dto: ResponseDTO = {
          documentURL: this.documentURL.toString()
      };
      return dto;
    }
    }
  
  Response.prototype.toString = function toString(): string {
    return `GetPresignedDownloadURLForIndividualSubmittedResponses(documentURL='${this.documentURL}')`;
  };
  

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    InvalidRespondentId = "INVALID_RESPONDENT_ID",
    SurveyNotSubmitted = "SURVEY_NOT_SUBMITTED"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface InvalidRespondentIdDTO {
      code: string;
    }
    
    export class InvalidRespondentId extends LeoRPCError<RPCError> {
      readonly isInvalidRespondentId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidRespondentId;
        super(code);
      }
    
    
      toDTO(): InvalidRespondentIdDTO {
        const dto: InvalidRespondentIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidRespondentId.prototype.toString = function toString(): string {
      return `GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.INVALID_RESPONDENT_ID()`;
    };
    
    
    export interface SurveyNotSubmittedDTO {
      code: string;
    }
    
    export class SurveyNotSubmitted extends LeoRPCError<RPCError> {
      readonly isSurveyNotSubmitted: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyNotSubmitted;
        super(code);
      }
    
    
      toDTO(): SurveyNotSubmittedDTO {
        const dto: SurveyNotSubmittedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyNotSubmitted.prototype.toString = function toString(): string {
      return `GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.SURVEY_NOT_SUBMITTED()`;
    };
    

    export type Errors = InvalidProjectId | InvalidRespondentId | SurveyNotSubmitted;
    export type ErrorsDTO = InvalidProjectIdDTO | InvalidRespondentIdDTO | SurveyNotSubmittedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.InvalidRespondentId:
          return new InvalidRespondentId();
        case RPCError.SurveyNotSubmitted:
          return new SurveyNotSubmitted();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestProjectIdError extends GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}

export class InvalidRequestRespondentIdError extends GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRespondentIdError";
  }
}

export class InvalidResponseDocumentUrlError extends GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDocumentUrlError";
  }
}

export class InvalidResponseInvalidProjectIdError extends GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseInvalidRespondentIdError extends GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidRespondentIdError";
  }
}

export class InvalidResponseSurveyNotSubmittedError extends GetPresignedDownloadURLForIndividualSubmittedResponsesRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyNotSubmittedError";
  }
}

